import React, { useState } from 'react';
import styled from 'styled-components';
import { initialsOf } from 'utils/staff-name';

// 12 fixed avatar gradients used as the fallback fill when a staff member has
// no photo. Keyed by a caller-supplied index (staff index or type color).
export const STAFF_GRADS = [
  'linear-gradient(135deg,#5B7FFF,#2E4ECC)',
  'linear-gradient(135deg,#FF8A65,#E64A19)',
  'linear-gradient(135deg,#9C6ADE,#6B3FA0)',
  'linear-gradient(135deg,#26A69A,#00695C)',
  'linear-gradient(135deg,#42A5F5,#1565C0)',
  'linear-gradient(135deg,#EC407A,#AD1457)',
  'linear-gradient(135deg,#FFB74D,#EF6C00)',
  'linear-gradient(135deg,#66BB6A,#2E7D32)',
  'linear-gradient(135deg,#7E8CE0,#3F51B5)',
  'linear-gradient(135deg,#8D6E63,#4E342E)',
  'linear-gradient(135deg,#29B6F6,#0277BD)',
  'linear-gradient(135deg,#AB47BC,#6A1B9A)',
];

interface Props {
  name: string;
  /** Staff photo URL. When missing (or it fails to load) gradient initials are shown. */
  avatar?: string;
  /** Keys the fallback gradient (staff index / type color). Falls back to name length. */
  gradIndex?: number;
  size?: number;
  fontSize?: number;
  className?: string;
  style?: React.CSSProperties;
}

const Circle = styled.span<{ $size: number; $fontSize: number; $grad: string }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: ${p => p.$fontSize}px;
  line-height: 1;
  flex-shrink: 0;
  background: ${p => p.$grad};
`;

const Photo = styled.img<{ $size: number }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
  display: inline-block;
  background: #F2F4F8;
`;

/** Staff avatar: the photo when available, otherwise gradient initials. */
const StaffAvatar: React.FC<Props> = ({ name, avatar, gradIndex, size = 36, fontSize = 13, className, style }) => {
  const [photoFailed, setPhotoFailed] = useState(false);

  if (avatar && !photoFailed) {
    return (
      <Photo
        src={avatar}
        alt={name}
        $size={size}
        className={className}
        style={style}
        onError={() => setPhotoFailed(true)}
      />
    );
  }

  const idx = Math.abs(gradIndex ?? name.length) % STAFF_GRADS.length;
  return (
    <Circle $size={size} $fontSize={fontSize} $grad={STAFF_GRADS[idx]} className={className} style={style}>
      {initialsOf(name)}
    </Circle>
  );
};

export default StaffAvatar;
